/**
 * Principal Observers - Sanjaya Brand Kit
 * Navy (#254B70), Orange (#F0A820), Teal (#258010)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  Users, UserPlus, UserMinus, Search, CheckCircle, XCircle,
  Clock, BookOpen, Calendar, MapPin, Mail, Phone, RefreshCw,
  AlertCircle, Award, Brain, BarChart3,
  Home, FileText, Eye, ClipboardList, UserCheck, Shield
} from 'lucide-react';
import ResponsiveLayout from '../components/ResponsiveLayout';

import { principalNavItems } from '../config/principalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrincipalObservers = () => {
  const navigate = useNavigate();
  const [myObservers, setMyObservers] = useState([]);
  const [availableObservers, setAvailableObservers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState('my');
  const [assigning, setAssigning] = useState(null);
  const [principalName, setPrincipalName] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('principal_token');
    if (!token) {
      navigate('/principal/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('principal_token');
      const [dashRes, myRes, availableRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/principal/dashboard?token=${token}`).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/principal/observers`, { params: { token } }),
        axios.get(`${BACKEND_URL}/api/principal/available-observers`, { params: { token } })
      ]);
      
      setPrincipalName(dashRes.data?.principal?.name || 'Principal');
      setMyObservers(myRes.data.observers || []);
      setAvailableObservers(availableRes.data.observers || []);
    } catch (error) {
      console.error('Error loading observers:', error);
    } finally {
      setLoading(false);
    }
  };

  const assignObserver = async (observerId) => {
    setAssigning(observerId);
    try {
      const token = localStorage.getItem('principal_token');
      await axios.post(`${BACKEND_URL}/api/principal/assign-observer`, null, {
        params: { token, observer_id: observerId }
      });
      await loadData();
      alert('Observer assigned successfully!');
    } catch (error) {
      console.error('Error assigning observer:', error);
      alert(error.response?.data?.detail || 'Failed to assign observer');
    } finally {
      setAssigning(null);
    }
  };

  const unassignObserver = async (observerId) => {
    if (!confirm('Are you sure you want to unassign this observer?')) return;
    
    setAssigning(observerId);
    try {
      const token = localStorage.getItem('principal_token');
      await axios.post(`${BACKEND_URL}/api/principal/unassign-observer`, null, {
        params: { token, observer_id: observerId }
      });
      await loadData();
      alert('Observer unassigned successfully!');
    } catch (error) {
      console.error('Error unassigning observer:', error);
      alert(error.response?.data?.detail || 'Failed to unassign observer');
    } finally {
      setAssigning(null);
    }
  };

  const filteredMyObservers = myObservers.filter(obs =>
    obs.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    obs.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAvailableObservers = availableObservers.filter(obs =>
    obs.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    obs.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleLogout = () => {
    localStorage.removeItem('principal_token');
    navigate('/principal/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-sanjaya-navy/20 border-t-sanjaya-navy rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-base sm:text-lg text-slate-600 font-medium">Loading observers...</p>
        </div>
      </div>
    );
  }

  return (
    <ResponsiveLayout
      navItems={principalNavItems}
      userRole="principal"
      userName={principalName}
      onLogout={handleLogout}
    >
      <div data-testid="principal-observers-page" className="font-body">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-sanjaya-navy flex items-center gap-2 sm:gap-3">
              <Users className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-sanjaya-navy" />
              Manage Observers
            </h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1">Assign and manage observers in your network</p>
          </div>
          <Button 
            variant="outline" 
            onClick={loadData}
            className="self-start sm:self-auto h-11 px-4 border-sanjaya-navy/30 text-sanjaya-navy"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5 sm:mb-6">
          <div className="bg-sanjaya-navy/10 border-2 border-sanjaya-navy/30 rounded-xl p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-500">My Observers</p>
                <p className="text-xl sm:text-2xl font-bold text-sanjaya-navy font-heading">{myObservers.length}</p>
              </div>
              <Users className="w-6 h-6 sm:w-8 sm:h-8 text-sanjaya-navy/30" />
            </div>
          </div>
          <div className="bg-sanjaya-teal/10 border-2 border-sanjaya-teal/30 rounded-xl p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-500">Available</p>
                <p className="text-xl sm:text-2xl font-bold text-sanjaya-teal font-heading">{availableObservers.length}</p>
              </div>
              <UserPlus className="w-6 h-6 sm:w-8 sm:h-8 text-sanjaya-teal/30" />
            </div>
          </div>
          <div className="bg-sanjaya-navy/10 border-2 border-sanjaya-navy/30 rounded-xl p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-500">Trained</p>
                <p className="text-xl sm:text-2xl font-bold text-sanjaya-navy font-heading">
                  {myObservers.filter(o => o.training_completed).length}
                </p>
              </div>
              <Award className="w-6 h-6 sm:w-8 sm:h-8 text-sanjaya-navy/30" />
            </div>
          </div>
          <div className="bg-sanjaya-orange/20 border-2 border-sanjaya-orange/30 rounded-xl p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-slate-500">Accepting</p>
                <p className="text-xl sm:text-2xl font-bold text-sanjaya-orange font-heading">
                  {myObservers.filter(o => o.is_accepting_new_children).length}
                </p>
              </div>
              <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-sanjaya-orange/30" />
            </div>
          </div>
        </div>

        {/* Search and Tabs */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 mb-5 sm:mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search observers..."
              className="pl-10 h-11 border-2 border-slate-200 focus:border-sanjaya-navy"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant={view === 'my' ? 'default' : 'outline'}
              onClick={() => setView('my')}
              className={`text-xs sm:text-sm flex-1 sm:flex-none ${view === 'my' ? 'bg-sanjaya-navy hover:bg-sanjaya-navy-dark' : 'border-slate-300'}`}
            >
              <Users className="w-4 h-4 mr-2" />
              My ({myObservers.length})
            </Button>
            <Button
              variant={view === 'available' ? 'default' : 'outline'}
              onClick={() => setView('available')}
              className={`text-xs sm:text-sm flex-1 sm:flex-none ${view === 'available' ? 'bg-sanjaya-teal hover:bg-sanjaya-teal-dark' : 'border-slate-300'}`}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Available ({availableObservers.length})
            </Button>
          </div>
        </div>

        {/* My Observers List */}
        {view === 'my' && (
          <div className="space-y-3 sm:space-y-4">
            {filteredMyObservers.length === 0 ? (
              <Card className="border-2 border-slate-200">
                <CardContent className="p-8 sm:p-12 text-center">
                  <Users className="w-14 h-14 sm:w-16 sm:h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-base sm:text-lg font-bold text-slate-800 font-heading mb-2">No Observers Assigned</h3>
                  <p className="text-sm text-slate-600 mb-4">Browse available observers to add them to your network.</p>
                  <Button onClick={() => setView('available')} className="bg-sanjaya-teal hover:bg-sanjaya-teal-dark">
                    <UserPlus className="w-4 h-4 mr-2" /> Browse Available
                  </Button>
                </CardContent>
              </Card>
            ) : (
              filteredMyObservers.map((observer) => (
                <Card key={observer.id} className="border-2 border-slate-200 hover:shadow-md transition-shadow">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-sanjaya-navy rounded-full flex items-center justify-center text-white text-lg sm:text-xl font-bold flex-shrink-0">
                          {observer.name?.charAt(0) || 'O'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <Link to={`/principal/observer/${observer.id}`} className="font-bold text-slate-800 hover:text-sanjaya-navy text-sm sm:text-base font-heading">
                              {observer.name}
                            </Link>
                            {observer.training_completed && (
                              <span className="flex items-center gap-1 text-xs bg-sanjaya-teal/10 text-sanjaya-teal px-2 py-0.5 rounded-full font-semibold">
                                <Award className="w-3 h-3" /> Trained
                              </span>
                            )}
                            {observer.is_accepting_new_children ? (
                              <span className="flex items-center gap-1 text-xs bg-sanjaya-navy/10 text-sanjaya-navy px-2 py-0.5 rounded-full font-semibold">
                                <CheckCircle className="w-3 h-3" /> Accepting
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                                <XCircle className="w-3 h-3" /> Not Accepting
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-500 mb-2">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3 sm:w-4 sm:h-4" /> {observer.email}
                            </span>
                            {observer.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3 sm:w-4 sm:h-4" /> {observer.phone}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-500">
                            {observer.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3 sm:w-4 sm:h-4" /> {observer.city}, {observer.state}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3 sm:w-4 sm:h-4" /> {observer.assigned_children || 0} children
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => unassignObserver(observer.id)}
                        disabled={assigning === observer.id}
                        className="text-red-600 border-red-300 hover:bg-red-50 self-start"
                      >
                        <UserMinus className="w-4 h-4 mr-1" />
                        {assigning === observer.id ? 'Removing...' : 'Remove'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Available Observers List */}
        {view === 'available' && (
          <div className="space-y-3 sm:space-y-4">
            {filteredAvailableObservers.length === 0 ? (
              <Card className="border-2 border-slate-200">
                <CardContent className="p-8 sm:p-12 text-center">
                  <AlertCircle className="w-14 h-14 sm:w-16 sm:h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-base sm:text-lg font-bold text-slate-800 font-heading mb-2">No Available Observers</h3>
                  <p className="text-sm text-slate-600">There are no unassigned observers at this time.</p>
                </CardContent>
              </Card>
            ) : (
              filteredAvailableObservers.map((observer) => (
                <Card key={observer.id} className="border-2 border-slate-200 border-l-4 border-l-sanjaya-teal hover:shadow-md transition-shadow">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-sanjaya-teal rounded-full flex items-center justify-center text-white text-lg sm:text-xl font-bold flex-shrink-0">
                          {observer.name?.charAt(0) || 'O'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="font-bold text-slate-800 text-sm sm:text-base font-heading">{observer.name}</span>
                            {observer.training_completed ? (
                              <span className="flex items-center gap-1 text-xs bg-sanjaya-teal/10 text-sanjaya-teal px-2 py-0.5 rounded-full font-semibold">
                                <Award className="w-3 h-3" /> Fully Trained
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-xs bg-sanjaya-orange/20 text-sanjaya-orange px-2 py-0.5 rounded-full font-semibold">
                                <Clock className="w-3 h-3" /> {observer.training_modules_completed}/6 Trained
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-500 mb-2">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3 sm:w-4 sm:h-4" /> {observer.email}
                            </span>
                            {observer.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3 sm:w-4 sm:h-4" /> {observer.phone}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-500">
                            {observer.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3 sm:w-4 sm:h-4" /> {observer.city}, {observer.state}
                              </span>
                            )}
                            {observer.qualification && (
                              <span className="flex items-center gap-1">
                                <BookOpen className="w-3 h-3 sm:w-4 sm:h-4" /> {observer.qualification}
                              </span>
                            )}
                            {observer.experience_years > 0 && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3 sm:w-4 sm:h-4" /> {observer.experience_years} yrs exp
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={() => assignObserver(observer.id)}
                        disabled={assigning === observer.id}
                        className="bg-sanjaya-teal hover:bg-sanjaya-teal-dark text-white self-start"
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        {assigning === observer.id ? 'Assigning...' : 'Assign to Me'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </ResponsiveLayout>
  );
};

export default PrincipalObservers;
